import React, { useRef, useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Loader2, User, Shield, Bell, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";
import ClaudeConnectorCard from "@/components/claude/ClaudeConnectorCard";
import SecurityPostureCard from "@/components/SecurityPostureCard";

function MfaCard({ user, refresh }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle"); // idle | enroll | codes
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mfa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    try { const r = await api.post("/mfa/totp/enroll/start"); setQr(r.data.qr_data_url); setManualKey(r.data.manual_key); setStep("enroll"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start setup"); }
    finally { setBusy(false); }
  };
  const verifyEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mfa/totp/enroll/verify", { code: code.trim() });
      setCodes(r.data.recovery_codes); setStep("codes"); setCode(""); refresh?.(); load();
      toast.success("Two-factor authentication is on");
    } catch (e) { toast.error(e.response?.data?.detail || "That code didn't match"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (!window.confirm("Turn off two-factor authentication?")) return;
    setBusy(true);
    try { await api.post("/mfa/disable", { password: pw || null }); toast.success("MFA disabled"); setPw(""); setStep("idle"); refresh?.(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not disable MFA"); }
    finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try { const r = await api.post("/mfa/recovery/regenerate", { password: pw || null }); setCodes(r.data.recovery_codes); setStep("codes"); setPw(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not regenerate codes"); }
    finally { setBusy(false); }
  };

  const enabled = status?.totp_enabled;
  return (
    <Card className="p-6" data-testid="mfa-card">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Two-factor authentication</h2>
        {enabled && <span data-testid="mfa-enabled-badge" className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">On</span>}
      </div>
      <p className="text-sm text-slate-500 mb-4">Add a second step at sign-in with an authenticator app (Google Authenticator, 1Password, Authy) or an emailed code. It layers on top of your normal login.</p>

      {step === "codes" && codes ? (
        <div data-testid="mfa-recovery-codes" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-900 mb-1">Save your recovery codes</div>
          <p className="text-xs text-amber-800 mb-3">Each works once if you lose your authenticator. They won't be shown again.</p>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-sm text-slate-800">
            {codes.map((c) => <div key={c} className="bg-white rounded px-2 py-1 border border-amber-100">{c}</div>)}
          </div>
          <Button data-testid="mfa-codes-done" className="mt-3 bg-[#003E87] hover:bg-[#00305F] text-white" onClick={() => { setCodes(null); setStep("idle"); }}>I've saved them</Button>
        </div>
      ) : step === "enroll" ? (
        <div className="flex flex-col sm:flex-row gap-6" data-testid="mfa-enroll-panel">
          <img src={qr} alt="Scan with your authenticator app" className="w-40 h-40 border border-slate-200 rounded-lg" />
          <div className="flex-1 space-y-2">
            <p className="text-xs text-slate-500">Scan the QR code, or enter this key manually:</p>
            <code className="text-xs bg-slate-100 rounded px-2 py-1 break-all block" data-testid="mfa-manual-key">{manualKey}</code>
            <Label className="text-xs">Enter the 6-digit code</Label>
            <Input data-testid="mfa-enroll-code" value={code} inputMode="numeric" onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="font-mono tracking-widest max-w-[160px]" />
            <div className="flex gap-2 pt-1">
              <Button data-testid="mfa-enroll-verify" onClick={verifyEnroll} disabled={busy || code.length !== 6} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Verify &amp; turn on</Button>
              <Button variant="outline" onClick={() => { setStep("idle"); setCode(""); }}>Cancel</Button>
            </div>
          </div>
        </div>
      ) : enabled ? (
        <div className="space-y-3 max-w-lg">
          <div className="text-sm text-slate-600">Authenticator app is active · {status?.recovery_remaining ?? 0} recovery code{status?.recovery_remaining === 1 ? "" : "s"} left.</div>
          {user?.has_password && <Input data-testid="mfa-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password (to change MFA)" className="max-w-xs" />}
          <div className="flex gap-2">
            <Button variant="outline" data-testid="mfa-regen-codes" onClick={regen} disabled={busy}>Regenerate recovery codes</Button>
            <Button variant="outline" data-testid="mfa-disable" onClick={disable} disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50">Turn off MFA</Button>
          </div>
        </div>
      ) : (
        <Button data-testid="mfa-setup-btn" onClick={start} disabled={busy} className="bg-[#003E87] hover:bg-[#00305F] text-white">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Set up authenticator app
        </Button>
      )}
    </Card>
  );
}

const NOTIF = [
  { key: "daily_digest", label: "Daily morning digest", help: "What needs action today, one email at 7am Central." },
  { key: "deadline_reminders", label: "Deadline reminders", help: "A nudge when a bid is due today, tomorrow or past." },
  { key: "weekly_worklog", label: "Weekly work recap", help: "Friday recap of what you did this week." },
  { key: "client_opens", label: "Client opened or downloaded", help: "The moment a client opens their proposal link or downloads a document from a bid you are on. On by default." },
];

function OpsEmailCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [coldDays, setColdDays] = useState(120);
  useEffect(() => { api.get("/ops/settings").then((r) => { setS(r.data); setColdDays(r.data.cold_days ?? 120); }).catch(() => {}); }, []);
  if (!s?.can_edit) return null;
  const saveCold = async () => {
    try { const r = await api.put("/ops/settings", { cold_days: coldDays }); setS(r.data); setColdDays(r.data.cold_days); toast.success(`Cold shelf set to ${r.data.cold_days} days`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const run = async () => {
    setBusy("dl");
    try {
      const r = await api.post("/ops/deadline-reminders/preview");
      const sent = r.data.emails_sent > 0;
      toast[sent || !r.data.flagged ? "success" : "error"](!r.data.flagged ? "No unsent bids are at their internal date today" : sent ? `${r.data.flagged} flagged bid(s) sent to your inbox` : "Email isn't configured here, so nothing was sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(""); }
  };
  return (
    <Card className="p-6" data-testid="ops-email-card">
      <div className="flex items-center gap-2 mb-2"><Bell className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Leadership emails</h2></div>
      <p className="text-sm text-slate-500 mb-4">Sent at 7:00 AM office time ({s.office_timezone}).</p>
      <p className="text-xs text-slate-400 mb-4 max-w-lg">The daily internal-deadline reminder always goes to the assigned estimator and the BDM.</p>
      <div className="flex items-center justify-between gap-4 max-w-lg mb-4 pt-3 border-t border-slate-100" data-testid="ops-cold-shelf">
        <div>
          <div className="text-sm text-slate-700">Cold shelf</div>
          <div className="text-[11px] text-slate-400">A sent bid parks as cold after this many days with no client touch (last client email, call or meeting; the send date if they never replied). A new client email brings it back.</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Input type="number" min={s.cold_days_range?.[0]} max={s.cold_days_range?.[1]} value={coldDays} onChange={(e) => setColdDays(Number(e.target.value))}
            className="w-20 h-8 text-sm font-mono" data-testid="ops-cold-days" />
          <span className="text-xs text-slate-500">days</span>
          <Button size="sm" variant="outline" className="h-8" onClick={saveCold} disabled={Number(coldDays) === Number(s.cold_days)} data-testid="ops-cold-days-save">Save</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" data-testid="ops-deadline-preview" disabled={!!busy} onClick={run}>{busy === "dl" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Send me today's deadline reminder</Button>
      </div>
    </Card>
  );
}

// Owner-only, company-wide "fresh start": wipe the Email Agent inbox + all project/client context
// history so nothing stale carries into a redeploy. Requires a typed RESET confirmation.
function ResetCard() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [archives, setArchives] = useState([]);
  const [restoring, setRestoring] = useState(null);
  const loadArchives = () => api.get("/admin/reset-archives").then((r) => setArchives(r.data || [])).catch(() => {});
  useEffect(() => { loadArchives(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-email-context", { confirm: "RESET" });
      const d = r.data?.deleted || {};
      toast.success(`Fresh start done — cleared ${d.emails || 0} emails, ${d.activities || 0} activities, ${d.notes || 0} notes. Recoverable for 30 days.`);
      setOpen(false); setConfirm(""); loadArchives();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
    finally { setBusy(false); }
  };
  const restore = async (batchId) => {
    setRestoring(batchId);
    try {
      const r = await api.post("/admin/reset-restore", { batch_id: batchId });
      const d = r.data?.restored || {};
      toast.success(`Restored ${d.emails || 0} emails, ${d.activities || 0} activities, ${d.notes || 0} notes.`);
      loadArchives();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
    finally { setRestoring(null); }
  };
  return (
    <Card className="p-6 border-rose-200" data-testid="reset-email-context-card">
      <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-rose-600" /><h2 className="font-semibold text-black">Reset email &amp; context history</h2></div>
      <p className="text-sm text-slate-600 mb-4">
        Company-wide fresh start. Clears <b>every email in the Email Agent inbox</b>, all logged
        calls/meetings/notes/tasks, project notes &amp; comments, and pending suggested moves — and resets email
        sync so re-linking starts clean. Your projects, clients, pricing, takeoffs, documents, team and audit
        trail are kept. <b>Wiped emails &amp; notes are kept in a recoverable archive for 30 days</b>, so an
        accidental reset can be undone below.
      </p>
      <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="reset-email-context-open" onClick={() => setOpen(true)}>
        Reset email &amp; context…
      </Button>

      {archives.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="reset-archives">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Recoverable resets</div>
          <div className="space-y-2">
            {archives.map((a) => (
              <div key={a.batch_id} data-testid={`reset-archive-${a.batch_id}`} className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-700">{a.at ? new Date(a.at).toLocaleString() : "Recent reset"} · {a.by_name || "Owner"}</div>
                  <div className="text-xs text-slate-400">
                    {a.counts?.emails || 0} emails · {a.counts?.activities || 0} activities · {a.counts?.notes || 0} notes · {a.counts?.suggestions || 0} moves
                    {a.days_left != null ? ` · ${a.days_left}d left to restore` : ""}
                  </div>
                </div>
                <Button size="sm" variant="outline" data-testid={`reset-restore-${a.batch_id}`} disabled={!!restoring} onClick={() => restore(a.batch_id)}
                  className="h-8 text-[#003E87] border-[#003E87]/30 hover:bg-[#003E87]/5">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> {restoring === a.batch_id ? "Restoring…" : "Restore"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setConfirm(""); } }}>
        <DialogContent data-testid="reset-email-context-dialog">
          <DialogHeader>
            <DialogTitle>Type RESET to confirm</DialogTitle>
            <DialogDescription>This wipes all emails and context history for the whole company. It's recoverable for 30 days from the Settings page. Projects, pricing and takeoffs are kept.</DialogDescription>
          </DialogHeader>
          <Input data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" className="font-mono tracking-widest" />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => { setOpen(false); setConfirm(""); }}>Cancel</Button>
            <Button disabled={busy || confirm !== "RESET"} onClick={run} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reset-confirm-btn">
              {busy ? "Resetting…" : "Reset everything"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({ name: user?.name || "", title: user?.title || "", phone: user?.phone || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [notif, setNotif] = useState(() => Object.fromEntries(NOTIF.map((x) => [x.key, !!user?.notify?.[x.key]])));
  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated");
      refresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put("/auth/profile", profile); toast.success("Profile saved"); refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingProfile(false); }
  };

  const savePw = async () => {
    if (pw.next.length < 12) { toast.error("Password must be at least 12 characters"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { new_password: pw.next, current_password: pw.current || null });
      toast.success(user?.has_password ? "Password changed" : "Password set");
      setPw({ current: "", next: "" }); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update password"); }
    finally { setSavingPw(false); }
  };

  const toggleNotif = async (key, val) => {
    const next = { ...notif, [key]: val };
    setNotif(next);
    try { const r = await api.put("/me/notify", { [key]: val }); setNotif((n) => ({ ...n, ...r.data.notify })); refresh?.(); }
    catch { setNotif(notif); toast.error("Could not save preference"); }
  };

  return (
    <div className="max-w-[1500px] w-full mx-auto px-6 py-8 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-black">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your profile, security and notifications.</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5"><User className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Profile</h2></div>
        <div className="flex items-start gap-6">
          <div className="relative shrink-0">
            <Avatar className="w-20 h-20"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#003E87] text-white text-lg">{initials}</AvatarFallback></Avatar>
            <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="absolute -bottom-1 -right-1 w-8 h-8 grid place-items-center rounded-full bg-[#F4C400] text-black shadow hover:bg-[#D9AE00]">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="avatar-file-input" onChange={onAvatar} />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Full name</Label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
            <div><Label className="text-xs">Title</Label><Input data-testid="profile-title" value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder="e.g. Chief Estimator" /></div>
            <div><Label className="text-xs">Phone</Label><Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
            <div><Label className="text-xs">Email</Label><Input value={user?.email || ""} disabled className="bg-slate-50" /></div>
            <div className="col-span-2 flex items-center justify-between">
              <span className="text-xs text-slate-400">Role: {ROLE_LABELS[user?.role] || user?.role}</span>
              <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Save profile</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5"><Shield className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Security</h2></div>
        <div className="grid grid-cols-2 gap-4 max-w-lg">
          {user?.has_password && (
            <div className="col-span-2"><Label className="text-xs">Current password</Label>
              <Input data-testid="settings-current-pw" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></div>
          )}
          <div className="col-span-2"><Label className="text-xs">{user?.has_password ? "New password" : "Set a password"}</Label>
            <Input data-testid="settings-new-pw" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="At least 12 characters" /></div>
          <div className="col-span-2"><Button data-testid="settings-save-pw" onClick={savePw} disabled={savingPw} variant="outline">{user?.has_password ? "Change password" : "Set password"}</Button></div>
        </div>
      </Card>

      <div id="mfa"><MfaCard user={user} refresh={refresh} /></div>

      {(user?.role === "owner" || user?.role === "admin") && <SecurityPostureCard user={user} />}

      <OpsEmailCard />

      <ClaudeConnectorCard />

      {user?.role === "owner" && <ResetCard />}

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1"><Bell className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Notifications</h2></div>
        <p className="text-xs text-slate-500 mb-5">Every email GridForge sends on a schedule has its own switch. All are off until you turn them on.</p>
        <div className="space-y-3">
          {NOTIF.map((n) => (
            <div key={n.key} className="flex items-center justify-between gap-6 max-w-lg">
              <div><div className="text-sm text-slate-700">{n.label}</div><div className="text-[11px] text-slate-400">{n.help}</div></div>
              <Switch data-testid={`notif-${n.key}`} checked={!!notif[n.key]} onCheckedChange={(v) => toggleNotif(n.key, v)} />
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}


